/**
 * localCache.js — IndexedDB helper for the local-first policy.
 *
 * Wraps IndexedDB in a promise-based key/value API so React hooks can
 * cache API payloads on-device. First paint after login becomes
 * instant, and read-only screens keep working when the backend is
 * unreachable (offline mode / airplane wifi / prod outage).
 *
 * Every cached value auto-expires — default 24 h — so we don't serve
 * stale month-old data.
 */

const DB_NAME = "crc_local_cache";
const STORE = "kv";
const DB_VERSION = 1;

let _db = null;
function open() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { resolve(null); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => { _db = req.result; resolve(req.result); };
  });
}

/**
 * Store a value under `key`. Optional TTL in milliseconds (default 24h).
 * Silently no-ops if IndexedDB is unavailable (Firefox private mode etc.)
 */
export async function put(key, value, ttlMs = 24 * 3600 * 1000) {
  try {
    const db = await open(); if (!db) return;
    const now = Date.now();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put({ key, value, expires_at: now + ttlMs, written_at: now });
      tx.oncomplete = resolve; tx.onerror = () => reject(tx.error);
    });
  } catch { /* silent — cache is opportunistic */ }
}

/** Read a value; returns null if missing or expired. */
export async function get(key) {
  try {
    const db = await open(); if (!db) return null;
    const doc = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    if (!doc) return null;
    if (doc.expires_at && doc.expires_at < Date.now()) return null;
    return doc.value;
  } catch { return null; }
}

/** Delete a single key. */
export async function del(key) {
  try {
    const db = await open(); if (!db) return;
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = resolve; tx.onerror = () => reject(tx.error);
    });
  } catch { /* silent */ }
}

/** Discard legacy private snapshots automatically when the session changes. */
export async function clearAll() {
  try {
    const db = await open(); if (!db) return;
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = resolve; tx.onerror = () => reject(tx.error);
    });
  } catch { /* Missing or unavailable storage contains no readable snapshot. */ }
}

/**
 * Convenience — "stale-while-revalidate": returns the cached value
 * (or null) immediately, and kicks off `fetcher()` in the background
 * to refresh. Calls `onFresh(value)` when the fresh value arrives.
 *
 * Usage:
 *   const cached = await getSWR("dashboard", () => api.get("/dashboard"), setData);
 *   if (cached) setData(cached);
 */
export async function getSWR(key, fetcher, onFresh, ttlMs) {
  const cached = await get(key);
  Promise.resolve().then(async () => {
    try {
      const fresh = await fetcher();
      const payload = fresh?.data !== undefined ? fresh.data : fresh;
      await put(key, payload, ttlMs);
      if (typeof onFresh === "function") onFresh(payload);
    } catch { /* offline — leave cache in place */ }
  });
  return cached;
}
